import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { useAuth0, RedirectLoginOptions, LogoutOptions, User } from '@auth0/auth0-react';
import { registerAuthTokenGetter } from '../lib/authToken';
import { env } from '../lib/env';
import { queryClient } from '../lib/queryClient';

const TOKEN_TIMEOUT_MS = 12000;
const AUTH_READY_POLL_MS = 100;
const AUTH_READY_MAX_POLLS = 40;

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    promise
      .then((value) => {
        window.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        window.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

export type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: User;
  error?: Error;
  login: (options?: RedirectLoginOptions) => Promise<void>;
  logout: (options?: LogoutOptions) => void;
  refreshSession: () => Promise<void>;
};

const demoUser = {
  sub: 'demo|e2e-user',
  email: 'demo@lighthouse.jobs',
  name: 'Demo User',
  nickname: 'demo.user',
  'https://lighthouse.jobs/roles': ['candidate', 'employer', 'admin'],
} as User & { 'https://lighthouse.jobs/roles': string[] };

const defaultAuth: AuthContextValue = {
  isAuthenticated: false,
  isLoading: true,
  user: undefined,
  error: undefined,
  login: async () => {},
  logout: () => {},
  refreshSession: async () => {},
};

const AuthContext = createContext<AuthContextValue>(defaultAuth);

export function AuthBridgeProvider({ children }: { children: ReactNode }) {
  const auth0 = useAuth0();
  const { getAccessTokenSilently, isAuthenticated } = auth0;
  const lastAuthIdentityRef = useRef<string | null>(null);
  const authStateRef = useRef({
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    getAccessTokenSilently: auth0.getAccessTokenSilently,
  });

  useEffect(() => {
    authStateRef.current = {
      isAuthenticated: auth0.isAuthenticated,
      isLoading: auth0.isLoading,
      getAccessTokenSilently: auth0.getAccessTokenSilently,
    };
  }, [auth0.getAccessTokenSilently, auth0.isAuthenticated, auth0.isLoading]);

  const waitForAuthInitialization = async () => {
    let attempts = 0;
    while (authStateRef.current.isLoading && attempts < AUTH_READY_MAX_POLLS) {
      await new Promise((resolve) => {
        window.setTimeout(resolve, AUTH_READY_POLL_MS);
      });
      attempts += 1;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const id = window.setInterval(() => {
      getAccessTokenSilently({ cacheMode: 'off' }).catch((error) => {
        console.warn('[auth] silent refresh failed', error);
      });
    }, 1000 * 60 * 10);
    return () => window.clearInterval(id);
  }, [getAccessTokenSilently, isAuthenticated]);

  useEffect(() => {
    registerAuthTokenGetter(async () => {
      await waitForAuthInitialization();
      if (!authStateRef.current.isAuthenticated) {
        throw new Error('Auth session is not authenticated yet.');
      }
      const audience = env.auth0Audience || undefined;
      return withTimeout(
        authStateRef.current.getAccessTokenSilently({ authorizationParams: { audience } }),
        TOKEN_TIMEOUT_MS,
        `Auth token request timed out after ${TOKEN_TIMEOUT_MS / 1000}s.`,
      ).catch((error) => {
        console.warn('[auth] token fetch failed', error);
        throw error;
      });
    });
    return () => {
      registerAuthTokenGetter(null);
    };
  }, []);

  useEffect(() => {
    const authIdentity = auth0.isAuthenticated ? auth0.user?.sub ?? '__authenticated__' : '__guest__';
    if (lastAuthIdentityRef.current === null) {
      lastAuthIdentityRef.current = authIdentity;
      return;
    }
    if (lastAuthIdentityRef.current !== authIdentity) {
      queryClient.clear();
    }
    lastAuthIdentityRef.current = authIdentity;
  }, [auth0.isAuthenticated, auth0.user?.sub]);

  const value: AuthContextValue = {
    isAuthenticated: auth0.isAuthenticated,
    isLoading: auth0.isLoading,
    user: auth0.user,
    error: auth0.error,
    login: async (options) => auth0.loginWithRedirect(options),
    logout: (options) => auth0.logout(options ?? { logoutParams: { returnTo: window.location.origin } }),
    refreshSession: () =>
      withTimeout(
        auth0.getAccessTokenSilently({ cacheMode: 'off' }),
        TOKEN_TIMEOUT_MS,
        `Session refresh timed out after ${TOKEN_TIMEOUT_MS / 1000}s.`,
      ).then(() => undefined),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function MockAuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    registerAuthTokenGetter(() => Promise.resolve('demo-token'));
    return () => registerAuthTokenGetter(null);
  }, []);
  return <AuthContext.Provider value={defaultAuth}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
